import React, { useEffect, useRef, useState, useContext } from "react";
import { useParams } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import { Grid, LinearProgress, Typography } from "@material-ui/core";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@material-ui/core";
import api from "../../services/api";
import { has, get, isNull } from "lodash";
import CardCounter from "../../components/Dashboard/CardCounter";
import GroupIcon from "@material-ui/icons/Group";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import DoneIcon from "@material-ui/icons/Done";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ListAltIcon from "@material-ui/icons/ListAlt";
import { useDate } from "../../hooks/useDate";
import { i18n } from "../../translate/i18n";

import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  textRight: {
    textAlign: "right",
  },
  tabPanelsContainer: {
    padding: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(3),
  },
  statusCell: {
    width: 140,
    whiteSpace: "nowrap",
  },
}));

const CampaignReport = () => {
  const classes = useStyles();

  const { campaignId } = useParams();

  const [campaign, setCampaign] = useState({});
  const [validContacts, setValidContacts] = useState(0);
  const [delivered, setDelivered] = useState(0);
  const [confirmationRequested, setConfirmationRequested] = useState(0);
  const [confirmed, setConfirmed] = useState(0);
  const [percent, setPercent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [shippingRows, setShippingRows] = useState([]);
  const mounted = useRef(true);
  const refreshTimeoutRef = useRef(null);

  const { datetimeToClient } = useDate();

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    if (mounted.current) {
      findCampaign();
    }

    return () => {
      mounted.current = false;
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mounted.current && has(campaign, "shipping")) {
      const shipping = get(campaign, "shipping") || [];

      if (has(campaign, "contactList")) {
        const contactList = get(campaign, "contactList");
        const contacts = contactList?.contacts || [];
        // Prioriza total real de destinatários planejados para manter o progresso consistente.
        setValidContacts(shipping.length > 0 ? shipping.length : contacts.length);
      }

      if (has(campaign, "shipping")) {
        const contacts = shipping;
        const delivered = contacts.filter((c) => c.status === "CONCLUÍDA");
        const confirmationRequested = contacts.filter(
          (c) => !isNull(c.confirmationRequestedAt)
        );
        const confirmed = contacts.filter(
          (c) => !isNull(c.deliveredAt) && !isNull(c.confirmationRequestedAt)
        );
        setDelivered(delivered.length);
        setConfirmationRequested(confirmationRequested.length);
        setConfirmed(confirmed.length);
        setDelivered(delivered.length);
      }
    }
  }, [campaign]);

  useEffect(() => {
    setPercent(validContacts > 0 ? (delivered / validContacts) * 100 : 0);
  }, [delivered, validContacts]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);
    const eventName = `company-${companyId}-campaign`;

    const handleCampaignSocket = (data) => {
      if (!data?.record || data.record.id !== +campaignId) {
        return;
      }

      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }

      refreshTimeoutRef.current = setTimeout(() => {
        if (mounted.current) {
          findCampaign();
        }
      }, 300);
    };

    socket.on(eventName, handleCampaignSocket);

    return () => {
      socket.off(eventName, handleCampaignSocket);
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, socketManager]);

  const findCampaign = async () => {
    setLoading(true);
    const { data } = await api.get(`/campaigns/${campaignId}`);
    setCampaign(data);

    const explicitRows = get(data, "shippingReport", null);
    if (Array.isArray(explicitRows)) {
      setShippingRows(explicitRows);
    } else {
      const fallbackRows = (get(data, "shipping", []) || []).map((row) => {
        const rawStatus = row?.status;
        let reportStatus = "aguardando";
        if (rawStatus === "CONCLUÍDA") {
          reportStatus = "sucesso";
        } else if (rawStatus === "ERRO" || rawStatus === "CANCELADA") {
          reportStatus = "erro";
        }

        return {
          id: row.id,
          contactName: row?.contact?.name || i18n.t("campaignReport.unnamedContact"),
          contactNumber: row?.number || row?.contact?.number || "",
          reportStatus,
          statusDescription:
            row?.statusDescription ||
            i18n.t("campaignReport.currentStatus", {
              status: rawStatus || i18n.t("common.unknown"),
            }),
        };
      });

      setShippingRows(fallbackRows);
    }

    setLoading(false);
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "sucesso":
        return i18n.t("campaignReport.sendStatuses.success");
      case "erro":
        return i18n.t("campaignReport.sendStatuses.error");
      default:
        return i18n.t("campaignReport.sendStatuses.waiting");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "sucesso":
        return "primary";
      case "erro":
        return "secondary";
      default:
        return "default";
    }
  };

  const formatStatus = (val) => {
    switch (val) {
      case "INATIVA":
        return i18n.t("campaigns.statuses.inactive");
      case "PROGRAMADA":
        return i18n.t("campaigns.statuses.scheduled");
      case "EM_ANDAMENTO":
        return i18n.t("campaigns.statuses.inProgress");
      case "CANCELADA":
        return i18n.t("campaigns.statuses.canceled");
      case "FINALIZADA":
        return i18n.t("campaigns.statuses.finished");
      default:
        return val;
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Grid style={{ width: "99.6%" }} container>
          <Grid xs={12} item>
            <Title>
              {i18n.t("campaignReport.title", {
                name: campaign.name || i18n.t("campaignReport.campaignFallback"),
              })}
            </Title>
          </Grid>
        </Grid>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Typography variant="h6" component="h2">
          {i18n.t("campaignReport.statusLine", {
            status: formatStatus(campaign.status) || "",
            delivered,
            total: validContacts,
          })}
        </Typography>
        <Grid spacing={2} container>
          <Grid xs={12} item>
            <LinearProgress
              variant="determinate"
              style={{ height: 15, borderRadius: 3, margin: "20px 0" }}
              value={percent}
            />
          </Grid>
          <Grid xs={12} md={4} item>
            <CardCounter
              icon={<GroupIcon fontSize="inherit" />}
              title={i18n.t("campaignReport.cards.targetContacts")}
              value={validContacts}
              loading={loading}
            />
          </Grid>
          {campaign.confirmation && (
            <>
              <Grid xs={12} md={4} item>
                <CardCounter
                  icon={<DoneIcon fontSize="inherit" />}
                  title={i18n.t("campaignReport.cards.confirmationRequested")}
                  value={confirmationRequested}
                  loading={loading}
                />
              </Grid>
              <Grid xs={12} md={4} item>
                <CardCounter
                  icon={<DoneAllIcon fontSize="inherit" />}
                  title={i18n.t("campaignReport.cards.confirmed")}
                  value={confirmed}
                  loading={loading}
                />
              </Grid>
            </>
          )}
          <Grid xs={12} md={4} item>
            <CardCounter
              icon={<CheckCircleIcon fontSize="inherit" />}
              title={i18n.t("campaignReport.cards.delivered")}
              value={delivered}
              loading={loading}
            />
          </Grid>
          {campaign.whatsappId && (
            <Grid xs={12} md={4} item>
              <CardCounter
                icon={<WhatsAppIcon fontSize="inherit" />}
                title={i18n.t("campaigns.table.whatsapp")}
                value={campaign.whatsapp?.name || "N/A"}
                loading={loading}
              />
            </Grid>
          )}
          {campaign.contactListId && (
            <Grid xs={12} md={4} item>
              <CardCounter
                icon={<ListAltIcon fontSize="inherit" />}
                title={i18n.t("campaigns.table.contactList")}
                value={campaign.contactList?.name || "N/A"}
                loading={loading}
              />
            </Grid>
          )}
          <Grid xs={12} md={4} item>
            <CardCounter
              icon={<ScheduleIcon fontSize="inherit" />}
              title={i18n.t("campaigns.table.scheduledAt")}
              value={datetimeToClient(campaign.scheduledAt)}
              loading={loading}
            />
          </Grid>
          <Grid xs={12} md={4} item>
            <CardCounter
              icon={<EventAvailableIcon fontSize="inherit" />}
              title={i18n.t("campaignReport.cards.completedAt")}
              value={datetimeToClient(campaign.completedAt)}
              loading={loading}
            />
          </Grid>
        </Grid>

        <TableContainer className={classes.tableContainer}>
          <Typography variant="h6" component="h3" style={{ marginBottom: 12 }}>
            {i18n.t("campaignReport.table.title")}
          </Typography>
          <Table size="small" aria-label={i18n.t("campaignReport.table.ariaLabel")}>
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("campaignReport.table.contact")}</TableCell>
                <TableCell>{i18n.t("campaignReport.table.number")}</TableCell>
                <TableCell className={classes.statusCell}>{i18n.t("campaignReport.table.sendStatus")}</TableCell>
                <TableCell>{i18n.t("campaignReport.table.statusDescription")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shippingRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    {i18n.t("campaignReport.table.empty")}
                  </TableCell>
                </TableRow>
              ) : (
                shippingRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.contactName || "-"}</TableCell>
                    <TableCell>{row.contactNumber || "-"}</TableCell>
                    <TableCell className={classes.statusCell}>
                      <Chip
                        size="small"
                        color={getStatusColor(row.reportStatus)}
                        label={getStatusLabel(row.reportStatus)}
                      />
                    </TableCell>
                    <TableCell>{row.statusDescription || "-"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </MainContainer>
  );
};

export default CampaignReport;
